import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {SignupFormEmailsUpdateInformation, SmartMailingListTooltipMap} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {ContactsTabSelection} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import type {MailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {smartMailingListsExtraReducers} from '@Components/mailing-list-contacts/mailing-list-contacts-extra-reducer';
import type {SignupForm} from '@/form-maker/grapesjs-form-maker/src/form-maker.types';
import type {SignupFormsResponse} from '@Components/mailing-list-contacts/mailing-list-contacts-thunk';
import type {RawAudience, UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';

export interface SmartMailingListsState {
  searchTerm: string | null;
  contactsTabSelection: ContactsTabSelection;
  mailingListSelected: MailingList | null;
  userMailingLists: MailingList[] | null;
  smartMailingLists: MailingList[] | null;
  signupForms: SignupForm[] | null;
  haveUserMailingListsLoaded: boolean;
  signupFormUpdateInformation: SignupFormEmailsUpdateInformation;
  userCustomFieldMeta: UserCustomFieldMeta[];
  shouldLoadMoreSignupForms: boolean;
  shouldLoadMoreMailingLists: boolean;
  hasMoreMailingListsOnServer: boolean;
  hasMoreSignupFormsOnServer: boolean;
  smartMailingListsTooltipMap: SmartMailingListTooltipMap | null;
  shouldShowSidebarActivityIndicator: boolean;
}

const initialState: SmartMailingListsState = {
  searchTerm: null,
  contactsTabSelection: ContactsTabSelection.MAILING_LISTS,
  mailingListSelected: null,
  userMailingLists: null,
  smartMailingLists: null,
  signupForms: null,
  haveUserMailingListsLoaded: false,
  signupFormUpdateInformation: {},
  userCustomFieldMeta: [],
  shouldLoadMoreMailingLists: false,
  shouldLoadMoreSignupForms: false,
  hasMoreMailingListsOnServer: false,
  hasMoreSignupFormsOnServer: false,
  smartMailingListsTooltipMap: null,
  shouldShowSidebarActivityIndicator: false,
};

export const smartMailingListsSlice = createSlice({
  name: 'mailingListContacts',
  initialState,
  reducers: {
    updateSearchTerm: (state, {payload}: PayloadAction<string | null>): void => {
      state.searchTerm = payload;
    },
    updateContactsTabSelection: (state, {payload}: PayloadAction<ContactsTabSelection>): void => {
      state.contactsTabSelection = payload;
    },
    updateMailingListSelection: (state, {payload}: PayloadAction<MailingList | null>): void => {
      state.mailingListSelected = payload;
    },
    updateUserMailingLists: (state, {payload}: PayloadAction<MailingList[] | null>): void => {
      state.userMailingLists = payload;
    },
    updateHaveUserMailingListsLoaded: (state, {payload}: PayloadAction<boolean>): void => {
      state.haveUserMailingListsLoaded = payload;
    },
    updateSmartMailingLists: (state, {payload}: PayloadAction<MailingList[] | null>): void => {
      state.smartMailingLists = payload;
    },
    updateSignupForms: (state, {payload}: PayloadAction<SignupForm[]>): void => {
      state.signupForms = payload;
    },
    updateSignupFormUpdateInformation: (state, {payload}: PayloadAction<SignupFormEmailsUpdateInformation>): void => {
      state.signupFormUpdateInformation = payload;
    },
    updateShouldLoadMoreMailingLists: (state, {payload}: PayloadAction<boolean>): void => {
      state.shouldLoadMoreMailingLists = payload;
    },
    updateShouldLoadMoreSignupForms: (state, {payload}: PayloadAction<boolean>): void => {
      state.shouldLoadMoreSignupForms = payload;
    },
    updateMailingListContacts: (state, {payload}: PayloadAction<MailingListContactUpdatePayload>): void => {
      const {mailingListId, contact} = payload;

      const list = state.userMailingLists?.find((list) => list.id === mailingListId);
      if (!list) {
        return;
      }

      if (!list.mailingListContacts) {
        list.mailingListContacts = [contact];
        return;
      }

      list.mailingListContacts.push(contact);
    },
    updateMailingListName: (state, {payload}: PayloadAction<MailingListNameUpdatePayload>): void => {
      if (state.mailingListSelected && state.mailingListSelected.id === payload.mailingListId) {
        state.mailingListSelected.name = payload.newName;
      }

      if (state.userMailingLists) {
        const mailingList = state.userMailingLists.find((list) => list.id === payload.mailingListId);
        if (mailingList) {
          mailingList.name = payload.newName;
        }
      }
    },
    addMailingList: (state, {payload}: PayloadAction<MailingList>): void => {
      if (!state.userMailingLists) {
        state.userMailingLists = [payload];
      } else {
        state.userMailingLists.unshift(payload);
      }
    },
    deleteMailingListWithId: (state, {payload}: PayloadAction<number>): void => {
      if (!state.userMailingLists) {
        return;
      }

      state.userMailingLists = state.userMailingLists.filter((list) => list.id !== payload);
    },
    addSignupForm: (state, {payload}: PayloadAction<SignupForm>): void => {
      if (!state.signupForms) {
        state.signupForms = [payload];
      }

      state.signupForms.push(payload);
    },
    deleteSignupFormWithId: (state, {payload}: PayloadAction<string>): void => {
      if (!state.signupForms) {
        return;
      }

      state.signupForms = state.signupForms.filter((list) => list.id !== payload);
    },
  },
  extraReducers: smartMailingListsExtraReducers,
});

export interface MailingListContactUpdatePayload {
  mailingListId: number;
  contact: RawAudience;
}

export interface MailingListNameUpdatePayload {
  mailingListId: number;
  newName: string;
}

export const handleSignupFormUpdate = (state: SmartMailingListsState, signupFormsResponse: SignupFormsResponse): void => {
  const signupForms = signupFormsResponse.signupForms;
  const signupFormsLength = signupForms.length;
  const updateInformation: SignupFormEmailsUpdateInformation = {};

  for (let i = 0; i < signupFormsLength; i++) {
    const updatedForm = signupForms[i];
    const idSignupForm = updatedForm.id;

    updateInformation[idSignupForm] = updatedForm.newEmailsCollected ?? 0;

    const existingForm = state.signupForms?.find((form) => form.id === idSignupForm);
    if (existingForm) {
      existingForm.emailsCollected = updatedForm.emailsCollected;
    }
  }

  state.signupFormUpdateInformation = updateInformation;
};

export const {
  updateSearchTerm,
  updateMailingListSelection,
  updateShouldLoadMoreMailingLists,
  updateShouldLoadMoreSignupForms,
  updateMailingListName,
  addMailingList,
  deleteMailingListWithId,
  addSignupForm,
  deleteSignupFormWithId,
} = smartMailingListsSlice.actions;

export const mailingListContactsReducer = smartMailingListsSlice.reducer;
